import * as React from "react";
import orderBy from 'lodash-es/orderBy';
import Loading, { FontSize } from "./loading";
import { DateTimeAccuracy, LocalizedDateTime } from "./LocalizedDateTime";
import { TextArea } from "../../forms/informedv4/TextAreaV4";
import { Form, useFormApi } from "informedv4";
import { ErrorBoundary } from "../../errors/ErrorBoundary";
import { TestNote, SessionNote, SubjectNote } from "webportalclient";
import Alert, { AlertCategoryEnum } from "./Alert";
import { sortBy, sortByDesc } from "../ArrayFunctions";
import { useSecurityContext } from "../../Core/SecurityContext";
import { faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface NotesModalBodyProps {
    notes: SessionNote[] | SubjectNote[] | TestNote[];
    isAddingNote: boolean;
    disabled: boolean;
    onEdit?: (id: string, comment: string) => void;
    onAdd: (annotation: string) => void;
    onCloseModal: () => void;
    errorStatus: string;
    changeButtonText?: string;
    comparisonNotes?: { title: string, items: SessionNote[] | SubjectNote[] | TestNote[] }[] | undefined;
    comparisonColumnHeader?: string;
}

interface NotesForm {
    Annotation: string;
}

export const NotesModalBodyForm = ({ notes, isAddingNote, disabled, onAdd, onEdit, onCloseModal, errorStatus, changeButtonText, comparisonNotes, comparisonColumnHeader }: NotesModalBodyProps) => {
    const formApi = useFormApi();
    const { currentUser } = useSecurityContext();
    const [noteToEdit, setNoteToEdit] = React.useState<SessionNote | SubjectNote | TestNote>();

    const validateMax1000Characters = value => {
        if (!value || value.trim() === '') {
            return "Annotation is required";
        }

        if (value.length > 1000)
            return "The Annotation field has a maximum length of 1000 characters";

        return undefined;
    };

    const onNoteSelection = (note) => {
        formApi.setValue("Annotation", note.comment);
        setNoteToEdit(note);
    }


    const renderRow = (noteGroup: { title: string, note: SessionNote }) => {
        return (
            <tr key={`Note${noteGroup.note.id}`}>
                {comparisonNotes?.length > 0 ? <td>{noteGroup.title}</td> : null}
                <td data-automationid={`notes-modal-user-${noteGroup.note.id}`}>{noteGroup.note.userEmailAddress}</td>
                <td style={{ whiteSpace: 'nowrap' }}><LocalizedDateTime automationId={`notes-modal-created-${noteGroup.note.id}`} time={noteGroup.note.recorded} accuracy={DateTimeAccuracy.Second} isTooltip={true} /></td>
                <td style={{ wordBreak: 'break-all' }} data-automationid={`notes-modal-comment-${noteGroup.note.id}`}>{noteGroup.note.comment}</td>
                {
                    disabled || !onEdit ? null :
                        <td data-automationid={`notes-modal-comment-edit-${noteGroup.note.id}`}>
                            {
                                currentUser.Email == noteGroup.note.userEmailAddress ?
                                    <button type="button" className="btn btn-primary" onClick={e => onNoteSelection(noteGroup.note)} disabled={noteToEdit && noteToEdit.id != noteGroup.note.id}>
                                        <FontAwesomeIcon icon={faPenToSquare} />
                                    </button>
                                    : ""
                            }
                        </td>
                }
            </tr>
        );
    }

    const handleSubmit = async () => {
        formApi.validate();
        const state = formApi.getFormState();

        if (state.invalid)
            return;

        if (noteToEdit) {
            handleUpdate(noteToEdit.id, state.values.Annotation)
        } else {
            handleAdd(state.values.Annotation)
        }


        formApi.setValue("Annotation", null);
    }

    const handleAdd = async (annotation) => {
        onAdd(annotation);
    }

    const handleUpdate = async (id, comment) => {
        onEdit(id, comment)
        setNoteToEdit(undefined);
    }

    let allNotes = [];
    if (comparisonNotes)
        allNotes = [
            { title: "Current", items: notes },
            ...comparisonNotes,
        ];
    else
        allNotes = [
            { title: "Current", items: notes },
        ];

    allNotes = allNotes.flatMap(({ title, items }) => items.map((item) => ({ title, note: item })))
        .sort(sortBy("note.recorded"));

    return (
        <ErrorBoundary>
            <div className="modal-body">
                {errorStatus ? <Alert category={AlertCategoryEnum.Danger} show={true}>{errorStatus}</Alert> : null}
                <table className="table">
                    <thead>
                        <tr>
                            {comparisonNotes?.length > 0 ? <th>{comparisonColumnHeader}</th> : null}
                            <th>User</th>
                            <th>Created</th>
                            <th>Note</th>
                            {
                                disabled || !onEdit ? null :
                                    <th>Edit</th>
                            }
                        </tr>
                    </thead>
                    <tbody>
                        {
                            allNotes.map(i => {
                                return renderRow(i);
                            })
                        }
                    </tbody>
                </table>
                {isAddingNote ? <Loading size={FontSize.OneX} /> : null}
                {
                    disabled ? null :
                        <div>
                            <label htmlFor="Annotation">{noteToEdit ? "Edit Note" : "Annotation"}</label>
                            <TextArea name="Annotation" field="Annotation" id="Annotation" value={noteToEdit?.comment} validate={validateMax1000Characters} style={{ resize: 'vertical' }} />
                        </div>
                }
            </div>
            <div className="modal-footer">
                <button className="btn btn-primary" type="button" disabled={isAddingNote || disabled} data-automationid="NotesModalAddButton"
                    onClick={handleSubmit}>
                    {changeButtonText != undefined ? changeButtonText : noteToEdit ? "Apply" : "Add"}
                </button>
                <button className="btn btn-primary btn-outline" data-automationid="NotesModalCancelButton" onClick={() => onCloseModal()}>
                    Cancel
                </button>
            </div>
        </ErrorBoundary>
    );
}

export const NotesModalBody = (props: NotesModalBodyProps) => {
    return (
        <Form validateOn="submit">
            <NotesModalBodyForm  {...props} />
        </Form>
    );
}
